import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg';
import { ReactComponent as IconTick } from "@/assets/svgs/tick.svg";
import InputPrefixIcon from '@/components/InputPrefixIcon/index';
import useDebounce from '@/hooks/use-debounce/index';
import usePrincipals from "@/hooks/use-principal/index";
import classname from "classnames";
import { useEffect, useState } from 'react';
import Loader from 'react-loader-spinner';
import ConfirmRemovalOfPrincipalModal from "../components/ConfirmRemovalOfPrincipalModal/index";
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature";
import Search from '@/componentsDS/Search/index';
import { ReactComponent as BlueIconInfo } from "@/assets/svgs/blue-icon.svg";
import ToggleSwitch3 from '@/components/ToggleSwitch3/index.js';
import SharePrincipalInfoModal from "../components/SharePrincipalInfoModal/index";
import useModal from "@/hooks/use-modal/index";
import OrgAPI from '@/services/api/org';
import PrincipalAPI from '@/services/api/principal.js'
import useToast from '@/hooks/use-toast/index';
import Toast from "@/components/Toast/index";
import { useDispatch, useSelector } from 'react-redux';
import { syncProfile } from '@/redux/actions/auth.js';



const Checkbox = ({ ['data-testid']: dataTestId, selected, handleUpdateItemSelect = null, disabled, indeterminate }) => {
	const checkBoxClass = classname({
		"use-case-request__checkbox": true,
		"use-case-request__checkbox--active": selected,
		"use-case-request__checkbox--disabled": disabled,
		"use-case-request__checkbox--indeterminate": indeterminate,
	});

	return (
		<div
			data-testid={dataTestId}
			className={checkBoxClass}
			onClick={() => handleUpdateItemSelect && handleUpdateItemSelect(!selected)}
		>
			{selected ? <IconTick /> : null}
		</div>
	);
};

const Page = ({ filteredPrincipalData, setFilteredPrincipalData, visibleRemovePrincipal, showRemovePrincipal, hideRemovePrincipal }) => {

	/*=================================================
		Search
	/================================================*/
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2);
	const profile = useSelector(state => state.auth.profile)	
	const isSharePrincipalEnabled = profile?.organization?.sharePrincipals ?? false


	const [searchBoxValue, setSearchBoxValue] = useState(undefined)
	const [toggle, setToggle] = useState(isSharePrincipalEnabled ?? false);
	
	const { toastList, addToastMessage } = useToast();
	const { visible: sharePrincipalInfoModal, show: showSharePrincipalInfoModal, hide: hideSharePrincipalInfoModal } = useModal()
	const dispatch = useDispatch()

	const debouncedFetchSortedData = useDebounce(getSortedData, "dataFiltering");

	function handleSearch(e) {
		setSearchBoxValue(e.target.value)
	}
	const handleCancel = () => {
		setSearchBoxValue("");
		getSortedData(1, "", undefined, undefined)
	}

	/*=================================================
		Principal
	/================================================*/

	const { fetch, deleteMany, items: principalItems, setItems: setPrincipalItems, fetchedItems: fetchedPrincipalItems, setFetchedItems: setFetchedPrincipalItems, meta, loading } = usePrincipals({ cacheFetchedItem: { identifier: 'id', keys: ['$selected'] } })

	useEffect(() => {
		getSortedData(1, undefined, undefined, undefined)
		defaultCurrentPage()
	}, [])

	useEffect(() => {
		if (fetchedPrincipalItems)
			setFilteredPrincipalData(fetchedPrincipalItems)
	}, [fetchedPrincipalItems])

	const removePrincipals = async () => {
		const principalIds = filteredPrincipalData.filter(s => s.$selected).map(s => s.id)
		const result = await deleteMany(principalIds)

		if (result) {
			if(isAgentPrincipalPhase2Enabled) {
				checkPrincipalCount();
			}
			hideRemovePrincipal()
			setFetchedPrincipalItems([])
			getSortedData(1, undefined, undefined, undefined)
			defaultCurrentPage()
		}
	}

	/*=================================================
		Sort
	/================================================*/

	const pageLimit = 10;
	const [sortConfig, setSortConfig] = useState({ sortBy: "", direction: false });

	function handleSort(sortBy) {
		let direction = sortConfig.direction;
		if (sortConfig.sortBy === sortBy) {
			direction = !direction;
		} else {
			direction = true;
		}

		setSortConfig({ sortBy, direction });
	}

	/*=================================================
		Pagination
	/================================================*/

	const [inputPage, setInputPage] = useState(1)

	useEffect(() => {
		if (principalItems?.length > 0 || searchBoxValue) {
			debouncedFetchSortedData()
		}
	}, [searchBoxValue])

	useEffect(() => {
		if (principalItems?.length) {
			getSortedData()
		}
	}, [sortConfig, inputPage])

	function defaultCurrentPage() {
		setInputPage(1)
	}

	function goToPrev() {

		if (inputPage === 1) {
			return
		}

		setInputPage(inputPage - 1)
	}

	function goToNext() {
		if (inputPage === meta?.pageCount) {
			return
		}
		setInputPage(inputPage + 1)
	}

	async function getSortedData(_currentPage = inputPage, _searchTerm = searchBoxValue, sortBy = sortConfig?.sortBy, direction = sortConfig?.direction) {
		await fetch({
			currentPage: _currentPage,
			pageSize: pageLimit,
			search: _searchTerm?.length > 0 ? _searchTerm : null,
			sortBy: sortBy?.length > 0 ? sortBy : null,
			direction: sortBy ? (direction ? "asc" : "desc") : null,
		})
	}

	/*=================================================
		Data Table
	/================================================*/

	function handleCurrentInput(e) {
		const value = e.target?.value
		const current = Number(value) || 1

		let newCurrent = current;
		if (current > meta.pageCount) {
			newCurrent = meta.pageCount
		} else if (current < 1) {
			newCurrent = 1
		}
		setInputPage(newCurrent)
	}

	function getClassName(name) {
		if (!sortConfig) {
			return;
		}

		const sortDirection = sortConfig.direction === true ? "is-asc" : "is-desc"

		return sortConfig.sortBy === name ? sortDirection : "";
	}

	const checkPrincipalCount = async() => {
		const [error, data] = await PrincipalAPI.index({params: {currentPage: 1, pageSize: 10}});
		if(!error && data?.meta?.count === 0 ) {
			updateSharePrincipalStatus(false);
		}
	}

	const updateSharePrincipalStatus = async(checked) => {
		const payload = {
			status: checked
		}
		const [error] = await OrgAPI.updateSharePrincipalStatus(payload);
		if(error) {
			return addToastMessage(error?.message ?? `${checked ? "Failed to share principal list" : "Failed to disable principal list sharing"}`, "error")
		}
		dispatch(syncProfile());
		setToggle(checked);
	}

	const DataTable = (itemData) => {
		const list = itemData && itemData?.length > 0 &&
			itemData?.map((item, index) => {
				return (
					<div className="data-table__table__body__row" key={index} onClick={(e) => changeListCheckboxes(e, index)}>
						<div className="data-table__table__body__content data-table__table__body__content--checkbox">
							<Checkbox
								index={index}
								data-testid={`principal-checkbox-${index}`}
								selected={item.$selected ?? false}
							/>
						</div>
						<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--heavy data-table__table__body__content--break data-table__flex">
							{item?.name}
						</div>
						<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--break">
							{item?.uen}
						</div>
					</div>
				);
			});

		function getPageStart(pageSize, pageNr) {
			return pageSize * pageNr;
		}

		function getPageLabel(total, pageSize, pageNr) {

			const start = Math.max(
				getPageStart(pageSize, pageNr),
				0
			);
			const end = Math.min(
				getPageStart(pageSize, pageNr + 1),
				total
			);

			return `${start + 1} - ${end}`;
		}

		const NoMessage = () => {
			return (
				<div className="data-table__empty__icon">
					<span>No principal added yet</span>
				</div>
			);
		};

		const isAllChecked = (itemData) => {
			return itemData?.length && itemData?.every((item) => item?.$selected === true);
		};

		const changeListCheckboxes = (e, index) => {

			setPrincipalItems(itemData.map((s, i) => {
				if (i === index) {
					s['$selected'] = !s['$selected']
				}

				return s
			}))
		};

		const changeAllListCheckboxes = (checked, itemData) => {
			if (itemData?.length) {
				setPrincipalItems(itemData?.map(s => {
					s.$selected = checked
					return s
				}))
			}
		};

		return (
			<div className="data-table__content">
				<div className="data-table__content__cols">
					<div className="data-table__content__col">
						<div className="data-table__table">
							<div className="data-table__table__head">
								<div className="data-table__table__head__row">
									<div className="data-table__table__head__title data-table__table__head__title--checkbox">
										<Checkbox
											name="All"
											id="All"
											data-testid="principal-checkbox"
											selected={isAllChecked(itemData)}
											indeterminate={!isAllChecked(itemData) && itemData?.some(item => item.$selected)}
											handleUpdateItemSelect={(checked) =>
												changeAllListCheckboxes(checked, itemData)
											}
										/>
									</div>
									<div
										className={`data-table__table__head__title  data-table__table__head__title__sort-order link ${getClassName(
											"name"
										)}`}
										id="email"
										onClick={() => {
											handleSort("name");
										}}
									>
										Principal Name{" "}
										<span className="data-table__table__head__icon__sort-order"></span>
									</div>
									<div
										className={`data-table__table__head__title  data-table__table__head__title__sort-order link ${getClassName(
											"uen"
										)}`}
										id="created"
										onClick={() => {
											handleSort("uen");
										}}
									>
										UEN{" "}
										<span className="data-table__table__head__icon__sort-order"></span>
									</div>
								</div>
							</div>

							{loading ? <>
								<div className="data-table__empty">
									<Loader style={{ alignItems: 'center' }} type="ThreeDots" color="$grey50" height={20} width={30} />
								</div>
							</> : <>
								{list?.length > 0 ? (
									<div className="data-table__table__body">
										{list}
									</div>
								) : (
									<div className="data-table__empty">
										<NoMessage />
									</div>
								)}
							</>}


							{
								list?.length > 0 && (
									<div className="data-table__table__footer">
										<div className="data-table__table__footer__row">
											<div className="data-table__table__footer__row__left">
												<div className="data-table__table__pagination">
													<input data-testid="pagination-input" className="data-table__table__pagination__page" type="number" value={inputPage} onChange={(e) => handleCurrentInput(e)} min="1" />
													<span className="data-table__table__pagination__page__meta"> of {meta?.pageCount || 1} pages </span>
													<button data-testid="pagination-prev" className="data-table__table__pagination__prev" onClick={() => goToPrev()} disabled={inputPage === 1}><IconChevronDown /></button>
													<button data-testid="pagination-next" className="data-table__table__pagination__next" onClick={() => goToNext()} disabled={inputPage === meta?.pageCount}><IconChevronDown /></button>
												</div>
											</div>
											<div className="data-table__table__footer__row__right">
												<div className="data-table__table__meta">
													Showing {getPageLabel(meta?.count, pageLimit, inputPage - 1)} of {meta?.count || 0} entries
												</div>
											</div>
										</div>
									</div>
								)
							}
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
		<div className="agent_principal__search_container">
			<div className="use-cases__search__box">
				{
					feature(featureConstant.NEW_DESIGN_SYSTEM) ? (
						<div style={{width:'334px'}}>
							<Search value={searchBoxValue || ""}
							placeholder="Search by Principal name or UEN" onChange={handleSearch} onCancel={handleCancel} />
						</div>
					):(

						<InputPrefixIcon
							maxWidth="334"
							placeholder="Search by Principal name or UEN"
							icon={<IconSearch />}
							value={searchBoxValue || ""}
							onChange={(e) => { handleSearch(e) }}
						/>
					)
				}
				<div>
					{fetchedPrincipalItems?.filter(s => s?.$selected)?.length > 0 && (
						<span>{fetchedPrincipalItems?.filter(s => s?.$selected)?.length || 0} Selected</span>
					)}
				</div>
			</div>
			{isAgentPrincipalPhase2Enabled && (
					<div className="agent_principal__share_principal_list__toggle-container">
						<div
							className="agent_principal__share_principal__info"
							onClick={showSharePrincipalInfoModal}
							role="none"
							onKeyDown={showSharePrincipalInfoModal}
						>
							<BlueIconInfo />
						</div>
						<p
							className={`${
								!toggle
									? "agent_principal__share_principal_text__inactive"
									: ""
							}`}
						>
							Share Principal List
						</p>
						<ToggleSwitch3
							id="sharePrincipalListToggle"
							checked={toggle}
							onChange={updateSharePrincipalStatus}
							data-testid="share-principal-list-toggle"
						/>
					</div>
				)}
		</div>

			<div className="">
				{DataTable(principalItems)}
			</div>

			<ConfirmRemovalOfPrincipalModal
				visible={visibleRemovePrincipal}
				hide={hideRemovePrincipal}
				principals={filteredPrincipalData}
				selectedKey={'$selected'}
				selectedValue={true}
				submit={removePrincipals}
			/>
			{isAgentPrincipalPhase2Enabled && (
				<>
					<SharePrincipalInfoModal
						visible={sharePrincipalInfoModal}
						hide={hideSharePrincipalInfoModal}
					/>
					<Toast toastList={toastList} />
				</>
			)}
		</>
	)
}

export default Page
